import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { modifyAddressDrawerSections } from '@common/constants';
import  {
  useGetCountries,
  useGetZones,
  useModifyAddress
} from '@common/slices/drawer.slice';
import { IBaseDrawerProps, ModifyAddress } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { RootState } from '@core/redux/store';
import { IAbstractRecord } from '@models';
import { DEFAULT_ERROR_TEXT } from '@utils';

interface Props extends IBaseDrawerProps {
  addressId: string;
  addressDetails: any;
}

const ModifyAddressDrawer: React.FC<Props> = ({
  onSuccess,
  addressId,
  addressDetails
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );


  const [loadCountries, countriesResult] = useGetCountries.useLazy();
  const [loadZones, zonesResult] = useGetZones.useLazy();

  useEffect(() => {
    loadCountries();
    loadZones();
  }, []);

  const countriesData = countriesResult?.data;
  const zonesData = zonesResult?.data;


  const [modifyAddress] = useModifyAddress();


  const [formState, setFormState] = useState<IAbstractRecord>({
    id: addressDetails?.id,
    type: addressDetails?.addressType,
    country: addressDetails?.country?.id,
    zone: addressDetails?.address?.zone,
    city: addressDetails?.address?.city,
    street: addressDetails?.address?.street,
    building: addressDetails?.address?.building,
    addressDetails: addressDetails?.address?.addressDetails,
    addressDetailsArabic: addressDetails?.address?.addressDetailsArabic
  });

  const countryCode = formState.country;


  const lovs = useMemo(() => {
    const countries =
      countriesData?.core?.getCountries?.reduce((acc: any, el: any) => {
        acc[el.id] = el.title;
        return acc;
      }, {}) || {};

    const zones =
      zonesData?.core?.getZones?.reduce((acc: any, el: any) => {
        acc[el.id] = el.title;
        return acc;
      }, {}) || {};

    return {
      addressTypes: {
        WORK: 'Work',
        HOME: 'Home'
      },
      countries,
      zones
    };
  }, [countriesData, zonesData]);


  const sections = useMemo(() => {
    return modifyAddressDrawerSections(formState, lovs, countryCode);
  }, [formState, lovs, countryCode]);


  const onChange = (field: string, value: any) => {
    setFormState((prev) => {
      const updated = {
        ...prev,
        [field]: value
      };

      if (field === 'country' && value !== 'LB') {
        updated.zone = null;
      }

      return updated;
    });
  };

  const onSubmit = async () => {
    try {
      const payload: ModifyAddress = {
        id: addressId,
        type: formState.type || null,
        country: formState.country || null,
        zone: formState.zone || null,
        city: formState.city || null,
        street: formState.street || null,
        building: formState.building || null,
        addressDetails: formState.addressDetails || null,
        addressDetailsArabic: formState.addressDetailsArabic || null,
        isPrimary: true
      };

      const res = await modifyAddress({ input: payload });

      if (!res?.errors) {
        onSuccess();
      }
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormWidget
      submitButtonState={submitButtonState}
      sections={sections}
      onSubmit={onSubmit}
      onChange={onChange}
    />
  );
};

export { ModifyAddressDrawer };
